// src/pages/LastCall.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { buildForfeoReservationUrl } from '../lib/forfeoHandoff';
import Header from '../components/Header';
import Footer from '../components/Footer';
import T from '../components/i18n/T';
import { ForfeoNetworkBadges } from '../components/network/ForfeoNetworkBadges';
import { ForfeoAppCTA } from '../components/network/ForfeoAppCTA';
import {
  FaFire, FaUtensils, FaBed,
  FaUserSecret, FaArrowRight, FaSearch, FaQuestionCircle, FaShieldAlt, FaBolt, FaStar,
  FaHeart, FaRegHeart, FaShareAlt, FaSortAmountDown, FaClock
} from 'react-icons/fa';

const HERO_IMAGE_URL = '/lastcall-hero.jpg';

// ======================
// CONFIGURATION ENCHÈRE
// ======================
const DROP_INTERVAL_MINUTES = 1; // Baisse 1$ par minute (pour le test)
const DROP_AMOUNT_DOLLARS = 1;

type DealPublic = {
  id: string;
  title: string;
  description: string;
  original_price: number | null;
  discount_price: number;
  quantity_available: number;
  expires_at: string;
  created_at: string; 
  image_url?: string | null;
  category_hint?: string | null;
  region_hint?: string | null;
  slot_start?: string | null;
  slot_end?: string | null;
  party_size?: number | null;
  business_id?: string;
};


const normalize = (s: string) =>
  (s || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

// ======================
// HOOKS : TICKER & DECOMPTE
// ======================

// Ce hook met à jour l'écran chaque seconde pour faire vivre le chrono de chute
const useEverySecond = () => {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick(x => x + 1), 1000);
    return () => clearInterval(t);
  }, []);
  return tick;
};

const CountdownTimer = ({ targetDate }: { targetDate: string }) => {
  const calc = () => {
    const diff = new Date(targetDate).getTime() - Date.now();
    if (diff <= 0) return null;
    return {
      h: Math.floor((diff / (1000 * 60 * 60)) % 24),
      m: Math.floor((diff / (1000 * 60)) % 60),
      s: Math.floor((diff / 1000) % 60)
    };
  };
  const [left, setLeft] = useState(calc());

  useEffect(() => {
    setLeft(calc());
    const t = setInterval(() => setLeft(calc()), 1000);
    return () => clearInterval(t);
  }, [targetDate]);

  if (!left) return <span className="text-red-500 font-bold"><T fr="Terminé" en="Ended" /></span>;

  if (left.h > 24) {
    return (
      <div className="flex gap-1 text-xs font-mono font-bold text-slate-900 bg-white/90 px-2 py-1 rounded backdrop-blur-sm shadow-sm">
        <span>{left.h}h restantes</span>
      </div>
    );
  }

  return (
    <div className="flex gap-1 text-xs font-mono font-bold text-slate-900 bg-white/90 px-2 py-1 rounded backdrop-blur-sm shadow-sm items-center">
      <span className="text-red-600 animate-pulse">●</span>
      <span className="text-red-600">EXP</span>
      <span>{String(left.h).padStart(2, '0')}h</span>
      <span>{String(left.m).padStart(2, '0')}m</span>
      <span>{String(left.s).padStart(2, '0')}s</span>
    </div>
  );
};


const buildForfeoLastCallReservationUrl = (deal: DealPublic) =>
  buildForfeoReservationUrl({
    target: 'last_call',
    id: deal.id,
    source: 'last_call',
    title: deal.title,
    region: deal.region_hint || undefined,
    partnerId: deal.business_id || undefined,
  });

const getMysteryInfo = (deal: DealPublic) => {
  const t = normalize(`${deal.title} ${deal.description}`);
  let img = 'https://images.unsplash.com/photo-1556740758-90de374c12ad?auto=format&fit=crop&w=1400&q=60';
  let name = 'Offre Exclusive';

  if (t.includes('hotel') || t.includes('hôtel') || t.includes('nuit') || t.includes('hebergement') || t.includes('hébergement')) {
    name = 'Hôtel Confidentiel';
    img = 'https://images.unsplash.com/photo-1566073771259-6a8506099945?auto=format&fit=crop&w=1400&q=60';
  } else if (t.includes('resto') || t.includes('restaurant') || t.includes('menu') || t.includes('table') || t.includes('restauration')) {
    name = 'Table de Chef';
    img = 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?auto=format&fit=crop&w=1400&q=60';
  }
  return { name, image: img };
};

type DealKind = 'all' | 'tables' | 'hotels';

const inferDealKind = (deal: DealPublic, businessCategory?: string | null): Exclude<DealKind, 'all'> | 'unknown' => {
  const hint = normalize(deal.category_hint || '');
  const text = normalize(`${deal.title} ${deal.description}`);
  const bcat = normalize(businessCategory || '');

  const isHotel =
    hint.includes('hebergement') || hint.includes('hotel') ||
    text.includes('hebergement') || text.includes('hotel') || text.includes('hôtel') ||
    text.includes('nuit') || bcat.includes('hebergement') || bcat.includes('hotel') || bcat.includes('hébergement');

  const isTable =
    hint.includes('restauration') || hint.includes('resto') || hint.includes('restaurant') ||
    hint.includes('table') || text.includes('resto') || text.includes('restaurant') ||
    text.includes('table') || text.includes('menu') || bcat.includes('restauration') || bcat.includes('restaurant');

  if (isHotel && !isTable) return 'hotels';
  if (isTable && !isHotel) return 'tables';
  if (isHotel) return 'hotels';
  if (isTable) return 'tables';
  return 'unknown';
};

const FaqSection = () => (
  <div className="mt-24 border-t border-slate-200 pt-16 pb-10 bg-slate-50/50">
    <div className="text-center mb-12">
      <div className="inline-flex items-center gap-2 bg-white border border-slate-200 text-slate-600 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest mb-4 shadow-sm">
        <FaQuestionCircle /> <T fr="Centre d'aide" en="Help Center" />
      </div>
      <h2 className="text-3xl font-black text-slate-900"><T fr="Questions Fréquentes" en="Frequently Asked Questions" /></h2>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto px-6">
      {[
        { icon: <FaSortAmountDown />, title: "Pourquoi le prix baisse-t-il ?", text: "C'est une enchère inversée ! Le prix chute à chaque minute. Mais attention, si vous attendez trop, quelqu'un d'autre réservera avant vous." },
        { icon: <FaUserSecret />, title: "Pourquoi l'identité est cachée ?", text: "Pour protéger l'image de marque de nos partenaires qui proposent ces tarifs confidentiels extrêmement bas." },
        { icon: <FaShieldAlt />, title: "Quand dois-je payer ?", text: "Le tarif est verrouillé au moment de la réservation et le paiement sécurisé se complète sur Forfeo.com." },
        { icon: <FaBolt />, title: "Est-ce immédiat ?", text: "Oui. Après paiement confirmé, Forfeo prépare le pass mobile, le QR sécurisé et la confirmation." }
      ].map((item, i) => (
        <div key={i} className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm hover:shadow-md transition flex gap-4">
          <div className="w-10 h-10 bg-slate-100 text-slate-900 rounded-full flex items-center justify-center shrink-0">{item.icon}</div>
          <div>
            <h4 className="font-bold text-slate-900 mb-1">{item.title}</h4>
            <p className="text-sm text-slate-500 leading-relaxed">{item.text}</p>
          </div>
        </div>
      ))}
    </div>
  </div>
);


export default function LastCall() {
  const [searchParams] = useSearchParams();
  const targetDealId = searchParams.get('id'); 
  
  // Refraichit l'UI toutes les secondes pour faire tourner le chrono d'enchère
  useEverySecond(); 

  const [deals, setDeals] = useState<DealPublic[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<DealKind>('all');
  const [q, setQ] = useState('');
  
  
  const [initialQtyById, setInitialQtyById] = useState<Record<string, number>>({});
  const [businessCategoryById, setBusinessCategoryById] = useState<Record<string, string>>({});
  
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [favoriteKeys, setFavoriteKeys] = useState<Set<string>>(new Set());

  // ✅ LOGIQUE CALCUL PRIX DYNAMIQUE & COMPTEUR
  const getLiveAuctionData = (deal: DealPublic) => {
    if (!deal.created_at) return { price: deal.discount_price, nextDropStr: null, isDropping: false }; 
    
    const floorPrice = Math.max(1, Math.floor(deal.discount_price * 0.5)); 
    
    const now = Date.now();
    const createdTime = new Date(deal.created_at).getTime();
    
    // Si l'offre vient du futur (bug d'horloge serveur), on empêche le crash
    if (now < createdTime) return { price: deal.discount_price, nextDropStr: "En attente...", isDropping: false };

    const elapsedMs = now - createdTime;
    const intervalMs = DROP_INTERVAL_MINUTES * 60 * 1000;
    
    const dropCount = Math.floor(elapsedMs / intervalMs);
    let currentPrice = deal.discount_price - (dropCount * DROP_AMOUNT_DOLLARS);
    
    if (currentPrice <= floorPrice) {
      return { price: floorPrice, nextDropStr: "Prix plancher atteint", isDropping: false };
    }
    
    const nextDropTime = createdTime + ((dropCount + 1) * intervalMs);
    const msUntilNextDrop = nextDropTime - now;
    
    const s = Math.floor((msUntilNextDrop / 1000) % 60);
    const m = Math.floor((msUntilNextDrop / (1000 * 60)) % 60);
    
    return {
      price: currentPrice,
      nextDropStr: `Chute dans ${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`,
      isDropping: true
    };
  };

  const fetchDeals = async () => {
    setLoading(true);
    
    const { data: authData } = await supabase.auth.getUser();
    const uid = authData?.user?.id || null;
    setCurrentUserId(uid);

    if (uid) {
      const { data: favs } = await supabase.from('finds_favorites').select('source_id').eq('user_id', uid).eq('source', 'last_call');
      if (favs) {
        setFavoriteKeys(new Set(favs.map(f => String(f.source_id))));
      }
    }

    const { data } = await supabase
      .from('last_call_deals_public')
      .select('*')
      .gt('quantity_available', 0)
      .gt('expires_at', new Date().toISOString());

    const list = (data || []) as DealPublic[];
    setDeals(list);
    setLoading(false);

    setInitialQtyById(prev => {
      const next = { ...prev };
      for (const d of list) {
        if (next[d.id] === undefined) next[d.id] = d.quantity_available;
        else if (d.quantity_available > next[d.id]) next[d.id] = d.quantity_available;
      }
      return next;
    });

    const ids = Array.from(new Set(list.map(d => d.business_id).filter(Boolean))) as string[];
    if (ids.length) {
      const { data: bs } = await supabase.from('businesses').select('id, category').in('id', ids);
      if (bs?.length) {
        setBusinessCategoryById(prev => {
          const next = { ...prev };
          for (const b of bs as any[]) next[b.id] = b.category || '';
          return next;
        });
      }
    }
  };

  useEffect(() => {
    fetchDeals();
    const sub = supabase.channel('deals-lastcall')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'last_call_deals' }, () => fetchDeals())
      .subscribe();
    return () => { supabase.removeChannel(sub); };
  }, []);

  useEffect(() => {
    if (deals.length > 0 && targetDealId) {
      const cleanTargetId = targetDealId.replace('lc_', ''); 
      const dealToOpen = deals.find(d => String(d.id) === cleanTargetId);
      
      if (dealToOpen) {
        window.location.assign(
          buildForfeoLastCallReservationUrl(
            dealToOpen,
          ),
        );
        return;
      }
    }
  }, [deals, targetDealId]);

  const filtered = useMemo(() => {
    const qq = normalize(q.trim());
    return deals.filter(d => {
      const kind = inferDealKind(d, businessCategoryById[d.business_id || ''] || null);
      const passKind = filter === 'all' ? true : filter === 'tables' ? kind === 'tables' : kind === 'hotels';
      if (!passKind) return false;
      if (!qq) return true;
      return normalize(`${d.title} ${d.description}`).includes(qq);
    });
  }, [deals, filter, q, businessCategoryById]);

  const counts = useMemo(() => {
    const c = { all: deals.length, tables: 0, hotels: 0 };
    for (const d of deals) {
      const kind = inferDealKind(d, businessCategoryById[d.business_id || ''] || null);
      if (kind === 'tables') c.tables++;
      if (kind === 'hotels') c.hotels++;
    }
    return c;
  }, [deals, businessCategoryById]);


  const handleToggleFavorite = async (e: React.MouseEvent, dealId: string) => {
    e.stopPropagation();
    if (!currentUserId) {
      alert("Veuillez vous connecter pour sauvegarder vos offres.");
      return;
    }

    const key = String(dealId);
    const isAdding = !favoriteKeys.has(key);

    setFavoriteKeys(prev => {
      const next = new Set(prev);
      isAdding ? next.add(key) : next.delete(key);
      return next;
    });

    try {
      if (!isAdding) {
        await supabase.from("finds_favorites").delete().eq("user_id", currentUserId).eq("source", "last_call").eq("source_id", key);
      } else {
        await supabase.from("finds_favorites").insert({ user_id: currentUserId, source: "last_call", source_id: key });
      }
    } catch {
      setFavoriteKeys(prev => {
        const next = new Set(prev);
        isAdding ? next.delete(key) : next.add(key);
        return next;
      });
    }
  };

  const handleShareClick = async (e: React.MouseEvent, dealId: string, title: string) => {
    e.stopPropagation();
    const shareUrl = `${window.location.origin}/last-call?id=${dealId}`;
    if (navigator.share) {
      try { await navigator.share({ title: `Offre Flash : ${title}`, text: `Regarde cette offre secrète en train de baisser : ${title}`, url: shareUrl }); } catch {}
    } else {
      navigator.clipboard.writeText(shareUrl);
      alert("Lien copié dans le presse-papier !");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 font-sans">
      <Header />
      <div className="pt-28 pb-16 px-4 bg-[#0B0F19] text-white relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMAGE_URL} alt="" className="w-full h-full object-cover opacity-20 scale-105" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0B0F19]/40 via-[#0B0F19]/80 to-[#0B0F19]" />
        </div>
        <div className="relative z-10 max-w-6xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-red-500/20 border border-red-500/50 px-4 py-1.5 rounded-full text-xs font-black uppercase tracking-widest mb-8 text-red-300 backdrop-blur-md">
            <span className="w-2 h-2 bg-red-500 rounded-full animate-ping"></span> <T fr="Enchères Inversées" en="Reverse Auctions" />
          </div>
          <h1 className="text-5xl md:text-7xl font-black mb-5 leading-tight tracking-tight">
            Last Call <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-400 to-orange-400">Live</span>.
          </h1>
          <p className="text-slate-300 text-lg md:text-xl max-w-3xl mx-auto mb-6 leading-relaxed">
            <T fr="Le prix de ces offres exclusives" en="The price of these exclusive offers" /> <span className="text-white font-black underline decoration-red-500"><T fr="baisse chaque minute" en="drops every minute" /></span><T fr=". Achetez maintenant ou prenez le risque que quelqu'un d'autre rafle la place !" en=". Buy now or risk someone else grabbing the spot!" />
          </p>
          <div className="mb-10 flex flex-col items-center gap-4">
            <ForfeoNetworkBadges variant="full" />
            <ForfeoAppCTA target="last_call" label="Ouvrir Last Call dans l’app" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            <div className="bg-white/5 border border-white/5 rounded-2xl p-6 backdrop-blur-md text-left">
              <div className="w-12 h-12 bg-slate-800 rounded-xl flex items-center justify-center text-2xl mb-4 text-red-400"><FaSortAmountDown /></div>
              <h3 className="font-bold text-lg mb-2"><T fr="Le Prix Chute" en="The Price Drops" /></h3>
              <p className="text-sm text-slate-300 leading-relaxed"><T fr="Attendez pour payer moins cher, mais attention aux ruptures de stock instantanées." en="Wait to pay less, but watch out for instant sell-outs." /></p>
            </div>
            <div className="bg-white/5 border border-white/5 rounded-2xl p-6 backdrop-blur-md text-left">
              <div className="w-12 h-12 bg-slate-800 rounded-xl flex items-center justify-center text-2xl mb-4">💎</div>
              <h3 className="font-bold text-lg mb-2"><T fr="Tarifs Privés" en="Private Rates" /></h3>
              <p className="text-sm text-slate-300 leading-relaxed"><T fr="Le nom du lieu reste secret jusqu'à la réservation pour protéger les hôtels et restos." en="The venue name stays hidden until reservation to protect hotels and restaurants." /></p>
            </div>
            <div className="bg-white/5 border border-white/5 rounded-2xl p-6 backdrop-blur-md text-left">
              <div className="w-12 h-12 bg-slate-800 rounded-xl flex items-center justify-center text-2xl mb-4">⚡️</div>
              <h3 className="font-bold text-lg mb-2"><T fr="Paiement sécurisé" en="Secure payment" /></h3>
              <p className="text-sm text-slate-300 leading-relaxed"><T fr="Verrouillez le prix actuel sur Forfeo.com, puis recevez votre pass mobile et votre QR sécurisé." en="Lock the current price on Forfeo.com, then receive your mobile pass and secure QR." /></p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-12 -mt-8 relative z-20">
        <div className="flex flex-col items-center gap-4 mb-10">
          <div className="flex justify-center gap-3">
            {['all', 'tables', 'hotels'].map((f) => (
              <button key={f} onClick={() => setFilter(f as DealKind)} className={`px-5 py-2.5 rounded-full text-sm font-bold transition flex items-center gap-2 shadow-sm ${filter === f ? 'bg-[#0B0F19] text-white ring-2 ring-red-500/50' : 'bg-white text-slate-600 hover:bg-slate-100'}`}>
                {f === 'all' ? 'Tout' : f === 'tables' ? <><FaUtensils className="text-xs"/> <T fr="Tables" en="Tables" /></> : <><FaBed className="text-xs"/> <T fr="Hôtels" en="Hotels" /></>}
                <span className="text-[10px] font-black opacity-60 bg-slate-100 px-2 py-0.5 rounded-full">{(counts as any)[f]}</span>
              </button>
            ))}
          </div>
          <div className="w-full max-w-xl">
            <div className="bg-white border border-slate-200 rounded-2xl px-4 py-3 flex items-center gap-3 shadow-sm">
              <FaSearch className="text-slate-400" />
              <input value={q} onChange={(e) => setQ(e.target.value)} className="w-full outline-none font-semibold text-slate-700" placeholder="Rechercher une offre..." />
            </div>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-20 text-slate-400 animate-pulse font-bold"><T fr="Récupération des enchères en direct..." en="Loading live auctions..." /></div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filtered.map(deal => {
              const info = getMysteryInfo(deal);
              
              // 🔴 Récupère le prix live ET l'info du prochain drop
              const { price: livePrice, nextDropStr, isDropping } = getLiveAuctionData(deal);
              
              const percent = deal.original_price ? Math.round(((deal.original_price - livePrice) / deal.original_price) * 100) : 0;
              const stockPercent = Math.max(2, (deal.quantity_available / (initialQtyById[deal.id] || 1)) * 100);
              const isFavorite = favoriteKeys.has(String(deal.id));

              return (
                <div key={deal.id} className="group bg-white rounded-3xl overflow-hidden shadow-lg border border-slate-100 hover:shadow-2xl transition-all flex flex-col h-full relative">
                  {/* BORDURE ANIMÉE SI LE PRIX BAISSE */}
                  {isDropping && <div className="absolute inset-0 border-2 border-red-500 rounded-3xl opacity-50 pointer-events-none animate-pulse z-50"></div>}
                  
                  <div className="h-64 relative bg-slate-900 overflow-hidden">
                    <img src={deal.image_url || info.image} className="w-full h-full object-cover opacity-80 blur-2xl scale-110" alt="" />
                    <div className="absolute inset-0 flex flex-col justify-between p-4">
                      
                      {/* HEADER DE LA CARTE */}
                      <div className="flex justify-between items-start gap-2">
                        <div className="bg-black/50 backdrop-blur-md text-white text-[10px] font-bold px-2 py-1.5 rounded flex items-center gap-1 shadow-sm border border-white/10">
                          <span className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse"></span> live
                        </div>
                        
                        <div className="flex items-center gap-2">
                          {percent > 0 && <div className="bg-red-600 text-white font-black text-xs px-2 py-1 rounded shadow-lg animate-pulse">-{percent}%</div>}
                          <button onClick={(e) => handleShareClick(e, deal.id, deal.title)} className="bg-white/90 hover:bg-white text-slate-700 rounded-full w-8 h-8 flex items-center justify-center shadow-md transition">
                            <FaShareAlt size={12} />
                          </button>
                          <button onClick={(e) => handleToggleFavorite(e, deal.id)} className="bg-white/90 hover:bg-white text-slate-900 rounded-full w-8 h-8 flex items-center justify-center shadow-md transition">
                            {isFavorite ? <FaHeart className="text-red-500" /> : <FaRegHeart className="text-slate-700" />}
                          </button>
                        </div>
                      </div>

                      <div className="self-center text-center">
                        <div className="inline-flex items-center gap-2 text-white/90 font-bold uppercase text-[10px] tracking-widest mb-2"><FaUserSecret /> <T fr="Offre Secrète" en="Secret Offer" /></div>
                        <h3 className="text-2xl font-black text-white leading-none drop-shadow-xl">{deal.title}</h3>
                      </div>
                      <div className="flex justify-end"><CountdownTimer targetDate={deal.expires_at} /></div>
                    </div>
                  </div>
                  <div className="p-6 flex-1 flex flex-col">
                    <p className="text-slate-600 text-sm mb-4 line-clamp-2">{deal.description}</p>
                    <ForfeoNetworkBadges variant="compact" className="mb-5" />
                    <div className="mb-6">
                      <div className="flex justify-between text-[10px] font-bold uppercase text-slate-400 mb-1">
                        <span className="text-red-500 flex items-center gap-1"><FaFire /> <T fr="Stock critique" en="Critical stock" /></span>
                        <span>{deal.quantity_available} places</span>
                      </div>
                      <div className="w-full bg-slate-100 h-1.5 rounded-full overflow-hidden">
                        <div className="bg-gradient-to-r from-red-500 to-orange-400 h-full transition-all" style={{ width: `${stockPercent}%` }} />
                      </div>
                    </div>
                    
                    {/* 🔴 ZONE PRIX ET CHRONO ENCHÈRE */}
                    <div className="flex items-end justify-between mt-auto pt-4 border-t border-slate-50">
                      <div>
                        {deal.original_price && <div className="text-slate-400 text-xs line-through font-bold">{deal.original_price}$</div>}
                        <div className="text-3xl font-black text-slate-900">{livePrice}$</div>
                        
                        <div className="mt-1">
                          {isDropping ? (
                            <span className="text-[10px] bg-red-50 text-red-600 border border-red-100 font-bold px-2 py-0.5 rounded flex items-center gap-1 w-fit">
                              <FaClock className="animate-pulse" /> {nextDropStr}
                            </span>
                          ) : (
                            <span className="text-[10px] text-slate-400 font-bold">
                              {nextDropStr}
                            </span>
                          )}
                        </div>
                      </div>
                      
                      <a
                        href={buildForfeoLastCallReservationUrl(deal)}
                        className="bg-red-600 text-white px-5 py-3 rounded-xl hover:bg-red-700 transition font-bold text-xs uppercase tracking-widest flex items-center gap-2 shadow-lg group-hover:scale-105 h-fit text-center"
                      >
                        <T fr="Continuer sur Forfeo.com" en="Continue on Forfeo.com" /> <FaArrowRight />
                      </a>
                    </div>
                    
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      <FaqSection />
      <Footer />
    </div>
  );
}
